import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Chip } from 'src/components/shared/Chip'
import { composedInvoiceStatusLabels } from 'src/constants/invoice/constants'
import { ComposedInvoiceStatus } from 'src/constants/invoice/enums'
import type { ComposedInvoice } from 'src/interfaces/invoice'
import { countPartnerInvoicesByStatus, deriveComposedInvoiceStatus } from 'src/utils/invoiceStatus'

interface Props {
  invoice: ComposedInvoice
}

// One chip replaces the five raw status columns Retool showed side by side. The detail panel
// keeps every underlying field, so nothing is hidden — this is a summary, not a substitute.
// `any` is the repo-sanctioned type for css/style props (CLAUDE.md, TypeScript) — a
// twin.macro `tw` template has no stable exported return type to annotate against.
const chipStyles: Record<ComposedInvoiceStatus, any> = {
  [ComposedInvoiceStatus.Canceled]: tw`bg-shades-100`,
  [ComposedInvoiceStatus.FundingFailed]: tw`bg-critical-50`,
  [ComposedInvoiceStatus.FundedAndPaid]: tw`bg-success-200`,
  [ComposedInvoiceStatus.Repaying]: tw`bg-primary-100`,
  [ComposedInvoiceStatus.AwaitingFunding]: tw`bg-warning-100`,
}

const chipTextStyles: Record<ComposedInvoiceStatus, any> = {
  [ComposedInvoiceStatus.Canceled]: tw`text-shades-700`,
  [ComposedInvoiceStatus.FundingFailed]: tw`text-critical-500`,
  [ComposedInvoiceStatus.FundedAndPaid]: tw`text-success-500`,
  [ComposedInvoiceStatus.Repaying]: tw`text-primary-700`,
  [ComposedInvoiceStatus.AwaitingFunding]: tw`text-shades-900`,
}

const ComposedInvoiceStatusCellComponent = ({ invoice }: Props) => {
  const status = useMemo(() => {
    return deriveComposedInvoiceStatus(invoice)
  }, [invoice])

  const counts = useMemo(() => {
    return countPartnerInvoicesByStatus(invoice)
  }, [invoice])

  // The progress states carry their counts so the chip never hides how far along a batch is.
  const label = useMemo(() => {
    if (status === ComposedInvoiceStatus.Repaying) {
      return `${composedInvoiceStatusLabels[status]} · ${counts.paid} of ${counts.total} paid`
    }

    if (status === ComposedInvoiceStatus.AwaitingFunding) {
      return `${composedInvoiceStatusLabels[status]} · ${counts.funded} of ${counts.total} funded`
    }

    return composedInvoiceStatusLabels[status]
  }, [status, counts])

  return (
    <Chip
      text={label}
      className={chipStyles[status]}
      textClassName={chipTextStyles[status]}
      dataTestId={`invoices-list-status-${invoice.id}`}
    />
  )
}

export const ComposedInvoiceStatusCell = memo(ComposedInvoiceStatusCellComponent)
